/**
 * Copyright (c) 2021 BlockDev AG
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React, { FC, useState, useEffect } from 'react';
import { Container, Col, Row } from 'react-bootstrap';
import { DashboardTile } from './DashboardTile';

import styles from './Dashboard.module.scss';
import { Button } from '../../components/Button/Button';
import { Footer } from '../../components/Footer/Footer';
import { claim, claimAllowed, getWeb3, loadState } from '../../helpers/helpers';
import { State } from '../../components/models';
import { formatMYST } from '../../components/utils';
import { useHistory } from 'react-router-dom';
import { PATHS } from '../../navigation/routes';
import { ErrorBlock } from '../../components/ErrorBlock/ErrorBlock';

export const Dashboard: FC = () => {
    const history = useHistory();
    const [state, setState] = useState<State>({
        claimed: undefined,
        wallet: undefined,
        data: undefined,
    });
    const [err, setErr] = useState<string | undefined>();

    const loadTransactorData = async () => {
        try {
            const result = await loadState();
            setState(result);
        } catch (err) {
            console.error('loadTransactorData', err);
            setErr("Wallet data not known. Make sure you're using Matic Mainnet in metamask");
        }
    };

    useEffect(() => {
        loadTransactorData();
    }, []);

    useEffect(() => {
        (window as any).ethereum
            .on('accountsChanged', async (accounts: Array<string>) => {
                console.log('accountsChanged', accounts);
                if (accounts === undefined || accounts.length === 0) {
                    setErr('Account disconnected');
                    return;
                }

                setState({ claimed: undefined, wallet: undefined, data: undefined });
                await loadTransactorData();
            })
            .on('disconnect', async () => {
                setErr('Lost connection to metamask');
            })
            .on('chainChanged', async () => {
                console.log('chainChanged');
                window.location.reload();
            });
    }, [(window as any).ethereum]);

    const handlePayout = async () => {
        const { wallet, data } = state;
        const web3 = await getWeb3();
        if (!web3 || !data || !wallet) {
            setErr('Something went wrong. Failed to load required data.');
            return;
        }

        try {
            const result = await claim(data.rewarderAddress, String(state.wallet), data.snapshot);
            history.push({
                pathname: PATHS.transactionComplete,
                state: {
                    error: null,
                    to: result.to,
                    transactionHash: result.transactionHash,
                },
            });
        } catch (e) {
            history.push({
                pathname: PATHS.transactionComplete,
                state: {
                    error: e,
                },
            });
            console.log(e);
            setErr('Could not submit transaction');
        }
    };

    if (err) {
        return (
            <div className={styles.wrapper}>
                <ErrorBlock message={err} showRefreshButton />
            </div>
        );
    }

    const shouldShowDashboard =
        state && state.data && state.data.snapshot && state.wallet && state.claimed;

    if (!shouldShowDashboard) {
        return (
            <div className={styles.wrapper}>
                <Container>Loading...</Container>
            </div>
        );
    }

    const isClaimAllowed = claimAllowed(state.data!, state.claimed!);

    return (
        <div className={styles.wrapper}>
            <Container>
                <div>
                    <img
                        className={styles.logo}
                        src="/assets/img/MysteriumLogo.svg"
                        alt="Mysterium Network Logo"
                    />
                </div>

                <Container className={styles.contents}>
                    <Row className={styles.headBlock}>
                        <h1>Your Earnings Report</h1>
                    </Row>

                    <Row className={styles.tileWrapper}>
                        <DashboardTile
                            title="Estimate Earnings"
                            value={formatMYST(state.data!.estimatedReward)}
                            icon="estimated-earnings-icon.svg"
                        />
                        <DashboardTile
                            title="Total Earnings"
                            value={formatMYST(state.data!.snapshot.total)}
                            icon="total-earning-icon.svg"
                        />

                        <DashboardTile
                            title="Total Paid"
                            value={formatMYST(state.claimed!)}
                            icon="total-paid-icon.svg"
                        />
                    </Row>

                    <Row>
                        <Col>
                            <Button
                                title={
                                    isClaimAllowed
                                        ? 'Claim Earnings'
                                        : 'There are no funds available to claim'
                                }
                                onClick={handlePayout}
                                className={styles.claimButton}
                                disabled={!isClaimAllowed}
                            ></Button>
                        </Col>
                    </Row>
                    <Row className={styles.footerBlock} />
                </Container>

                <Footer />
            </Container>
        </div>
    );
};
